// src/features/records/utils/dateRangeDisplay.js
import {
  buildDateKey,
  coerceDateValue,
  resolveLocale,
  resolveTimeZone,
} from '../../../utils/dateUtils';

/**
 * Format a DateRange for compact display.
 * @param {Object} dateRange - DateRange value to format
 * @param {Object} options - Formatter options
 * @param {string} options.locale - Explicit locale (required at callsites)
 * @param {string} options.timeZone - Optional fallback timezone
 * @returns {string} Formatted date string
 */
export const formatDateRange = (dateRange, { locale, timeZone } = {}) => {
  if (!dateRange?.start) return '';

  const start = coerceDateValue(dateRange.start);
  if (!start) return '';

  const end = coerceDateValue(dateRange.end);
  const isAllDay = dateRange.isAllDay === true;
  const localeCode = resolveLocale(locale);
  const rangeTimeZone = resolveTimeZone(dateRange.timezone);
  const fallbackTimeZone = resolveTimeZone(timeZone);
  const effectiveTimeZone = rangeTimeZone || fallbackTimeZone;

  // All-day ranges store an exclusive end at midnight; step back for display.
  const allDayEnd =
    isAllDay && end && end.getTime() > start.getTime()
      ? new Date(end.getTime() - 1)
      : end;

  const dateFormatter = new Intl.DateTimeFormat(localeCode, {
    month: 'short',
    day: 'numeric',
    ...(effectiveTimeZone ? { timeZone: effectiveTimeZone } : {}),
  });
  const dateTimeFormatter = new Intl.DateTimeFormat(localeCode, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(effectiveTimeZone ? { timeZone: effectiveTimeZone } : {}),
  });
  const timeFormatter = new Intl.DateTimeFormat(localeCode, {
    hour: 'numeric',
    minute: '2-digit',
    ...(effectiveTimeZone ? { timeZone: effectiveTimeZone } : {}),
  });

  const formatDate = (date) => dateFormatter.format(date);
  const formatDateTime = (date) => dateTimeFormatter.format(date);
  const formatTime = (date) => timeFormatter.format(date);
  const compareEnd = isAllDay ? allDayEnd : end;
  const isSameDay =
    compareEnd &&
    buildDateKey(start, { locale: localeCode, timeZone: effectiveTimeZone }) ===
      buildDateKey(compareEnd, { locale: localeCode, timeZone: effectiveTimeZone });

  if (isAllDay) {
    if (allDayEnd && !isSameDay) {
      return `${formatDate(start)} - ${formatDate(allDayEnd)}`;
    }
    return formatDate(start);
  }

  if (!end || end.getTime() === start.getTime()) {
    return formatDateTime(start);
  }

  if (isSameDay) {
    return `${formatDateTime(start)} - ${formatTime(end)}`;
  }

  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
};
